import { useEffect, useMemo, useState } from "react";
import { loadSession, login, logout, type Session } from "./api";

type Product = {
  name: string;
  category: "wellness" | "skin" | "family" | "home";
  detail: string;
  price: number;
  tone: string;
  form: string;
};

const products: Product[] = [
  {
    name: "Daily Greens",
    category: "wellness",
    detail: "30 servings · morning ritual",
    price: 68,
    tone: "sage",
    form: "jar"
  },
  {
    name: "Barrier Ritual Face Oil",
    category: "skin",
    detail: "30 ml · evening care",
    price: 84,
    tone: "sand",
    form: "dropper"
  },
  {
    name: "Little Ritual Mineral Bath",
    category: "family",
    detail: "12 oz · unscented",
    price: 32,
    tone: "clay",
    form: "pouch"
  },
  {
    name: "Quiet Home Concentrate",
    category: "home",
    detail: "16 fl oz · botanical",
    price: 28,
    tone: "mist",
    form: "bottle"
  }
];

const standards = [
  ["01", "Ingredient clarity", "A readable standard and a reason for every ingredient."],
  ["02", "Household-minded", "Considered for the people, pets, and spaces that share your home."],
  ["03", "Fewer, better things", "Useful essentials selected to earn their place in a daily ritual."]
];

export function App() {
  const [session, setSession] = useState<Session>({ authenticated: false });
  const [sessionReady, setSessionReady] = useState(false);
  const [filter, setFilter] = useState("all");
  const [cartCount, setCartCount] = useState(0);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    loadSession()
      .then(setSession)
      .catch(() => setSession({ authenticated: false }))
      .finally(() => setSessionReady(true));
  }, []);

  const visibleProducts = useMemo(
    () => products.filter((product) => filter === "all" || product.category === filter),
    [filter]
  );

  function addToBag(product: Product) {
    setCartCount((count) => count + 1);
    setNotice(`${product.name} was added to your local preview bag.`);
    window.setTimeout(() => setNotice(""), 3200);
  }

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">Skip to main content</a>
      <div className="announcement">
        <span>Complimentary shipping over $75</span>
        <i aria-hidden="true">✦</i>
        <span>Thoughtfully selected essentials</span>
      </div>
      <header className="site-header">
        <a className="brand" href="#top" aria-label="C57 Health and Beauty home">
          <span className="brand-mark">C<small>57</small></span>
          <span className="brand-name">C57 <small>Health &amp; Beauty</small></span>
        </a>
        <nav aria-label="Primary navigation">
          <a href="#shop">Shop</a>
          <a href="#standards">Our standards</a>
          <a href="#journal">Journal</a>
        </nav>
        <div className="header-actions">
          {sessionReady && session.authenticated ? (
            <button className="account-button" onClick={() => void logout(session)}>
              <span>Welcome, {session.display_name.split(" ")[0]}</span>
              <small>Sign out</small>
            </button>
          ) : (
            <button className="account-button" onClick={() => login()} disabled={!sessionReady}>
              <span>{sessionReady ? "Sign in" : "Checking…"}</span>
              <small>Private account</small>
            </button>
          )}
          <button className="bag-button" onClick={() => setNotice("Checkout arrives in the next commerce slice.")}>
            Bag <span>{cartCount}</span>
          </button>
        </div>
      </header>

      <main id="main">
        <section className="hero" id="top">
          <div className="hero-copy">
            <span className="eyebrow">The considered home</span>
            <h1>Feel good about<br /><em>what comes home.</em></h1>
            <p>
              Beautifully made essentials for skin, body, home, and the little people you love—selected
              with transparency and a high ingredient standard.
            </p>
            <div className="hero-actions">
              <a className="button button-dark" href="#shop">Shop the collection</a>
              <a className="text-link" href="#standards">Read our standards <span>→</span></a>
            </div>
            <div className="hero-proof">
              <div><strong>01</strong><span>Clear<br />standards</span></div>
              <div><strong>02</strong><span>Useful<br />rituals</span></div>
              <div><strong>03</strong><span>Calm<br />choices</span></div>
            </div>
          </div>
          <div className="hero-art" aria-label="A curated still life of C57 wellness products">
            <div className="sun-orbit" />
            <div className="leaf leaf-one" />
            <div className="leaf leaf-two" />
            <div className="product-form hero-bottle"><span>C57</span><small>daily<br />greens</small></div>
            <div className="product-form hero-dropper"><i /><span>C57</span></div>
            <div className="stone stone-one" />
            <div className="stone stone-two" />
            <p>Everyday essentials,<br /><em>beautifully considered.</em></p>
          </div>
        </section>

        <section className="collection" id="shop" aria-labelledby="collection-title">
          <div className="section-heading">
            <div><span className="eyebrow">The opening collection</span><h2 id="collection-title">Small rituals.<br />Meaningful standards.</h2></div>
            <p>A focused first shelf for the mornings, evenings, and family spaces that shape a well-lived day.</p>
          </div>
          <div className="filter-row" role="group" aria-label="Filter products">
            {["all", "wellness", "skin", "family", "home"].map((value) => (
              <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>
                {value === "all" ? "All essentials" : value}
              </button>
            ))}
          </div>
          <div className="product-grid">
            {visibleProducts.map((product) => (
              <article className="product-card" key={product.name}>
                <div className={`product-image ${product.tone}`}>
                  <span className="product-number">C57 / {String(products.indexOf(product) + 1).padStart(2, "0")}</span>
                  <div className={`product-object ${product.form}`}><span>C57</span><small>{product.category}</small></div>
                  <button onClick={() => addToBag(product)} aria-label={`Add ${product.name} to bag`}>+</button>
                </div>
                <div className="product-meta">
                  <div><span>{product.detail}</span><h3>{product.name}</h3></div>
                  <strong>${product.price}</strong>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="standards" id="standards" aria-labelledby="standards-title">
          <div className="standards-intro">
            <span className="eyebrow">Our point of view</span>
            <h2 id="standards-title">A shorter path to products you can feel good about.</h2>
            <p>We are building the information layer alongside the shelf: what it is, why it is here, and what matters when it enters your home.</p>
          </div>
          <div className="standards-list">
            {standards.map(([number, title, copy]) => (
              <article key={number}><span>{number}</span><div><h3>{title}</h3><p>{copy}</p></div></article>
            ))}
          </div>
        </section>

        <section className="journal" id="journal" aria-label="From the journal">
          <div className="journal-art"><div className="arch" /><div className="stem" /><div className="petal p1" /><div className="petal p2" /><div className="petal p3" /></div>
          <div className="journal-copy"><span className="eyebrow">From the journal</span><h2>The quiet work of choosing well.</h2><p>Our first field note looks at the questions behind a useful household standard—and how we turn those questions into a calmer shelf.</p><a href="#journal">Read field note 01 <span>→</span></a></div>
        </section>
      </main>

      <footer>
        <div className="footer-brand"><span className="brand-mark">C<small>57</small></span><p>Considered essentials for a well-lived home.</p></div>
        <div><strong>Explore</strong><a href="#shop">Collection</a><a href="#standards">Standards</a><a href="#journal">Journal</a></div>
        <div><strong>Account</strong><button onClick={() => login()}>Sign in</button></div>
        <p className="fine-print">Local implementation preview · Product content and prices are illustrative.</p>
      </footer>
      {notice && <div className="toast" role="status">{notice}</div>}
    </div>
  );
}
